// =============================================================================
// AFRILAB LIMS - Locations API Service
// =============================================================================

import { apiService } from './api';

// Types
export interface Location {
  id: string;
  code: string;
  name: string;
  type: 'MAIN_LAB' | 'SATELLITE';
  capabilities: string[];
  address?: string;
  city?: string;
  country?: string;
  vpn_ip?: string;
  api_endpoint?: string;
  is_active: boolean;
  receives_from?: string[];
  sends_to?: string;
  last_sync_at?: string;
  sync_status: string;
  created_at: string;
  updated_at: string;
}

export interface SampleTransfer {
  id: string;
  transfer_number: string;
  batch_id?: string;
  batch_number?: string;
  sample_ids: string[];
  sample_count: number;
  from_location_id: string;
  from_location_code: string;
  to_location_id: string;
  to_location_code: string;
  sent_by?: string;
  sent_by_name?: string;
  sent_at?: string;
  send_notes?: string;
  received_by?: string;
  received_by_name?: string;
  received_at?: string;
  receive_notes?: string;
  samples_received_count?: number;
  status: 'PENDING' | 'IN_TRANSIT' | 'RECEIVED' | 'PARTIAL' | 'CANCELLED';
  sync_status: string;
  created_at: string;
  updated_at: string;
}

export interface TransferResult {
  transfer: SampleTransfer;
  samples: any[];
  message: string;
}

export interface TransferStats {
  totalSent: number;
  totalReceived: number;
  pendingOut: number;
  pendingIn: number;
  averageTransitHours: number | null;
}

// =============================================================================
// LOCATIONS API
// =============================================================================

export const locationsService = {
  // Get all locations
  async getAll(activeOnly = false): Promise<Location[]> {
    const params = activeOnly ? '?active=true' : '';
    return apiService.get<Location[]>(`/locations${params}`);
  },

  // Get location by ID
  async getById(id: string): Promise<Location> {
    return apiService.get<Location>(`/locations/${id}`);
  },

  // Get location by code
  async getByCode(code: string): Promise<Location> {
    return apiService.get<Location>(`/locations/code/${code}`);
  },

  // Get current server location
  async getCurrent(): Promise<Location | null> {
    try {
      return await apiService.get<Location>('/locations/current');
    } catch {
      return null;
    }
  },

  // Get main labs
  async getMainLabs(): Promise<Location[]> {
    return apiService.get<Location[]>('/locations/main-labs');
  },

  // Get satellite locations
  async getSatellites(): Promise<Location[]> {
    return apiService.get<Location[]>('/locations/satellites');
  },

  // Get location stats
  async getStats(locationId: string): Promise<{
    pendingTransfersOut: number;
    pendingTransfersIn: number;
    lastSyncAt: string | null;
    syncStatus: string;
  }> {
    return apiService.get(`/locations/${locationId}/stats`);
  },

  // Get transfer destination for a location
  async getTransferDestination(locationId: string): Promise<Location | null> {
    try {
      return await apiService.get<Location>(`/locations/${locationId}/transfer-destination`);
    } catch {
      return null;
    }
  },

  // Get locations that send to this location
  async getTransferSources(locationId: string): Promise<Location[]> {
    return apiService.get<Location[]>(`/locations/${locationId}/transfer-sources`);
  },

  // Create location (admin only)
  async create(data: Partial<Location>): Promise<Location> {
    return apiService.post<Location>('/locations', data);
  },

  // Update location (admin only)
  async update(id: string, data: Partial<Location>): Promise<Location> {
    return apiService.put<Location>(`/locations/${id}`, data);
  },

  // Delete location (admin only)
  async delete(id: string): Promise<void> {
    return apiService.delete(`/locations/${id}`);
  },
};

// =============================================================================
// TRANSFERS API
// =============================================================================

export const transfersService = {
  // Initiate a transfer (from satellite)
  async initiateTransfer(data: {
    batch_id?: string;
    batch_number?: string;
    sample_ids: string[];
    from_location_id: string;
    to_location_id: string;
    notes?: string;
  }): Promise<TransferResult> {
    return apiService.post<TransferResult>('/transfers/initiate', data);
  },

  // Receive a transfer (at main lab)
  async receiveTransfer(data: {
    transfer_id?: string;
    sample_id?: string;
    notes?: string;
  }): Promise<TransferResult> {
    return apiService.post<TransferResult>('/transfers/receive', data);
  },

  // Receive by scanning a sample
  async receiveBySample(sampleId: string, notes?: string): Promise<TransferResult> {
    return apiService.post<TransferResult>(`/transfers/receive-by-sample/${sampleId}`, { notes });
  },

  // Cancel a transfer
  async cancelTransfer(transferId: string, reason?: string): Promise<SampleTransfer> {
    return apiService.put<SampleTransfer>(`/transfers/${transferId}/cancel`, { reason });
  },

  // Get transfer by ID
  async getById(id: string): Promise<SampleTransfer> {
    return apiService.get<SampleTransfer>(`/transfers/${id}`);
  },

  // Get transfer by number
  async getByNumber(transferNumber: string): Promise<SampleTransfer> {
    return apiService.get<SampleTransfer>(`/transfers/number/${transferNumber}`);
  },

  // Get pending outgoing transfers
  async getPendingFrom(locationId: string): Promise<SampleTransfer[]> {
    return apiService.get<SampleTransfer[]>(`/transfers/pending/from/${locationId}`);
  },

  // Get pending incoming transfers
  async getPendingTo(locationId: string): Promise<SampleTransfer[]> {
    return apiService.get<SampleTransfer[]>(`/transfers/pending/to/${locationId}`);
  },

  // Get transfer history
  async getHistory(locationId: string, limit = 50): Promise<SampleTransfer[]> {
    return apiService.get<SampleTransfer[]>(`/transfers/history/${locationId}?limit=${limit}`);
  },

  // Get transfer stats
  async getStats(locationId: string): Promise<TransferStats> {
    return apiService.get<TransferStats>(`/transfers/stats/${locationId}`);
  },
};

// =============================================================================
// SYNC API
// =============================================================================

export interface SyncStatus {
  enabled: boolean;
  locationType: string;
  locationCode: string;
  targetUrl: string;
  lastSyncAt: string | null;
  pendingCount: number;
  failedCount: number;
  isSyncing: boolean;
}

export interface SyncResult {
  pushed: number;
  pulled: number;
  failed: number;
  conflicts: number;
}

export const syncService = {
  // Get sync status
  async getStatus(): Promise<SyncStatus> {
    return apiService.get<SyncStatus>('/sync/status');
  },

  // Trigger manual sync
  async triggerSync(): Promise<SyncResult> {
    return apiService.post<SyncResult>('/sync/trigger', {});
  },

  // Retry failed syncs
  async retryFailed(): Promise<{ retried: number }> {
    return apiService.post('/sync/retry-failed', {});
  },

  // Get pending sync logs
  async getPendingLogs(limit = 100): Promise<any[]> {
    return apiService.get(`/sync/pending?limit=${limit}`);
  },

  // Get failed sync logs
  async getFailedLogs(limit = 100): Promise<any[]> {
    return apiService.get(`/sync/failed?limit=${limit}`);
  },
};

export default { locationsService, transfersService, syncService };
